import { useState, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { API, getAuthToken } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { useTableSort, SortableHeader } from "@/lib/tableSort";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Upload, Download, Search, X, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";

const BRANDS = ["Wellcore", "Dynamite", "Okami", "YouWeFit", "Zero Sugar", "OSMO"];

export default function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const bulkFileInputRef = useRef(null);
  
  // Search and Filter states
  const [searchQuery, setSearchQuery] = useState("");
  const [brandFilter, setBrandFilter] = useState("all");
  const [excludeFilter, setExcludeFilter] = useState("all");
  
  const [formData, setFormData] = useState({
    product_name: "",
    sku_id: "",
    item_code: "",
    brand: "",
    mrp: "",
    exclude_from_discount: false
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  // Filtered products based on search and filters
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Search filter (SKU Name, SKU ID, Item Code)
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery || 
        product.product_name.toLowerCase().includes(searchLower) ||
        product.sku_id.toLowerCase().includes(searchLower) ||
        (product.item_code && product.item_code.toLowerCase().includes(searchLower));
      
      // Brand filter
      const matchesBrand = brandFilter === "all" || product.brand === brandFilter;
      
      // Exclude from Discount filter
      const matchesExclude = excludeFilter === "all" || 
        (excludeFilter === "yes" && product.exclude_from_discount) ||
        (excludeFilter === "no" && !product.exclude_from_discount);
      
      return matchesSearch && matchesBrand && matchesExclude;
    });
  }, [products, searchQuery, brandFilter, excludeFilter]);

  // Sort: default Product Name A–Z; clickable headers cycle asc → desc → reset.
  const { sortedRows: sortedProducts, sort, toggleSort } = useTableSort(filteredProducts, {
    defaultKey: "product_name",
    defaultOrder: "asc",
    accessors: {
      product_name: (p) => p.product_name,
      sku_id: (p) => p.sku_id,
      brand: (p) => p.brand,
      mrp: (p) => Number(p.mrp) || 0,
      exclude_from_discount: (p) => !!p.exclude_from_discount,
    },
  });

  const clearFilters = () => {
    setSearchQuery("");
    setBrandFilter("all");
    setExcludeFilter("all");
  };

  const hasActiveFilters = searchQuery || brandFilter !== "all" || excludeFilter !== "all";

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      setProducts(response.data);
      setSelectedIds([]);
    } catch (error) {
      toast.error("Error fetching products");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(filteredProducts.map(p => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id, checked) => {
    if (checked) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds(selectedIds.filter(i => i !== id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    
    try {
      await Promise.all(
        selectedIds.map(id => 
          axios.delete(`${API}/products/${id}`, {
            headers: { Authorization: `Bearer ${getAuthToken()}` }
          })
        )
      );
      toast.success(`${selectedIds.length} product(s) deleted successfully`);
      fetchProducts();
    } catch (error) {
      toast.error("Error deleting products");
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingBulk(true);
    try {
      const formDataFile = new FormData();
      formDataFile.append("file", file);

      const response = await axios.post(`${API}/products/upload`, formDataFile, {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`
        }
      });

      toast.success(`Uploaded ${response.data.success} products successfully. ${response.data.failed} failed.`);
      if (response.data.errors.length > 0) {
        console.log("Upload errors:", response.data.errors);
      }
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error uploading file");
      console.error("Upload error:", error);
    } finally {
      setUploadingBulk(false);
      if (bulkFileInputRef.current) {
        bulkFileInputRef.current.value = "";
      }
    }
  };

  const downloadTemplate = () => {
    const csvContent = "product_name,sku_id,item_code,brand,mrp,exclude_from_discount\n" +
                       "Example Product,SKU001,ITEM001,Wellcore,100.00,No";
    
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "products_template.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        mrp: parseFloat(formData.mrp)
      };
      
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, payload, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        toast.success("Product updated successfully");
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${getAuthToken()}` }
        });
        toast.success("Product created successfully");
      }
      setDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "An error occurred");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;
    try {
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success("Product deleted successfully");
      fetchProducts();
    } catch (error) {
      toast.error("Error deleting product");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      product_name: product.product_name,
      sku_id: product.sku_id,
      item_code: product.item_code || "",
      brand: product.brand,
      mrp: product.mrp.toString(),
      exclude_from_discount: product.exclude_from_discount || false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      product_name: "",
      sku_id: "",
      item_code: "",
      brand: "",
      mrp: "",
      exclude_from_discount: false
    });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingProduct(null);
    resetForm();
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  const handleExport = () => {
    if (!sortedProducts.length) {
      toast.info("No products to export");
      return;
    }
    const header = ["SKU ID", "SKU Name", "Brand", "Item Code", "MRP", "Exclude from Discount", "Created At"];
    const rows = sortedProducts.map((p) => [
      String(p.sku_id ?? ""), p.product_name ?? "", p.brand ?? "", String(p.item_code ?? ""),
      Number(p.mrp) || 0, p.exclude_from_discount ? "Yes" : "No",
      p.created_at ? new Date(p.created_at).toLocaleString("en-IN") : "",
    ]);
    const ws = XLSX.utils.aoa_to_sheet([header, ...rows]);
    // Force SKU ID / Item Code cells to text so Excel never reformats them.
    rows.forEach((_, i) => [0, 3].forEach((c) => {
      const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c })];
      if (cell) { cell.t = "s"; cell.z = "@"; }
    }));
    ws["!cols"] = [{ wch: 22 }, { wch: 40 }, { wch: 18 }, { wch: 14 }, { wch: 10 }, { wch: 20 }, { wch: 22 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Products");
    const d = new Date();
    const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    XLSX.writeFile(wb, `OMNIX_Products_${stamp}.xlsx`);
    toast.success(`Exported ${rows.length} product${rows.length === 1 ? "" : "s"}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="products-title">Products</h1>
          <p className="text-muted-foreground mt-1">Manage your product catalog</p>
        </div>
        <div className="flex gap-2">
          <input
            type="file"
            ref={bulkFileInputRef}
            onChange={handleBulkUpload}
            accept=".csv,.xlsx,.xls"
            className="hidden"
          />
          <Button 
            onClick={() => bulkFileInputRef.current?.click()}
            variant="outline"
            disabled={uploadingBulk}
            data-testid="upload-products-button"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploadingBulk ? "Uploading..." : "Upload Products"}
          </Button>
          <Button 
            onClick={downloadTemplate}
            variant="outline"
            data-testid="download-template-button"
          >
            <Download className="h-4 w-4 mr-2" />
            Download Template
          </Button>
          <Button
            onClick={handleExport}
            variant="outline"
            disabled={loading}
            data-testid="export-products-button"
          >
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-accent hover:bg-accent/90 text-white" 
                data-testid="add-product-button"
                onClick={() => {
                  setEditingProduct(null);
                  resetForm();
                  setDialogOpen(true);
                }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle data-testid="product-dialog-title">{editingProduct ? "Edit Product" : "Add New Product"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="product_name">Product Name *</Label>
                  <Input
                    id="product_name"
                    value={formData.product_name}
                    onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                    required
                    data-testid="product-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="sku_id">SKU ID *</Label>
                  <Input
                    id="sku_id"
                    value={formData.sku_id}
                    onChange={(e) => setFormData({ ...formData, sku_id: e.target.value })}
                    required
                    data-testid="sku-id-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="item_code">Item Code *</Label>
                  <Input
                    id="item_code"
                    value={formData.item_code}
                    onChange={(e) => setFormData({ ...formData, item_code: e.target.value })}
                    required
                    placeholder="Unique item code"
                    data-testid="item-code-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="brand">Brand *</Label>
                  <Select
                    value={formData.brand}
                    onValueChange={(value) => setFormData({ ...formData, brand: value })}
                    required
                  >
                    <SelectTrigger data-testid="brand-select">
                      <SelectValue placeholder="Select a brand" />
                    </SelectTrigger>
                    <SelectContent>
                      {BRANDS.map((brand) => (
                        <SelectItem key={brand} value={brand} data-testid={`brand-option-${brand}`}>
                          {brand}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mrp">MRP *</Label>
                  <Input
                    id="mrp"
                    type="number"
                    step="0.01"
                    value={formData.mrp}
                    onChange={(e) => setFormData({ ...formData, mrp: e.target.value })}
                    required
                    data-testid="mrp-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="exclude_from_discount">Exclude from Discount Calculation</Label>
                  <Select
                    value={formData.exclude_from_discount ? "yes" : "no"}
                    onValueChange={(value) => setFormData({ ...formData, exclude_from_discount: value === "yes" })}
                  >
                    <SelectTrigger data-testid="exclude-discount-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="no">No</SelectItem>
                      <SelectItem value="yes">Yes</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    If "Yes", this SKU will be ignored in all discount calculations
                  </p>
                </div>
                <Button type="submit" className="w-full bg-accent hover:bg-accent/90 text-white" data-testid="submit-product-button">
                  {editingProduct ? "Update Product" : "Create Product"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>All Products ({filteredProducts.length})</CardTitle>
          {selectedIds.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="sm" data-testid="bulk-delete-products-button">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected ({selectedIds.length})
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Selected Products?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete {selectedIds.length} selected product(s)? This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleBulkDelete} className="bg-destructive hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </CardHeader>
        <CardContent>
          {/* Sticky Search and Filters */}
          <div className="sticky top-0 z-10 bg-white pb-4 mb-4 border-b">
            <div className="flex flex-wrap gap-3">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by SKU Name, SKU ID, or Item Code..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="products-search-input"
                />
              </div>
              
              {/* Brand Filter */}
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="w-[150px]" data-testid="brand-filter">
                  <SelectValue placeholder="Brand" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Brands</SelectItem>
                  {BRANDS.map(brand => (
                    <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {/* Exclude from Discount Filter */}
              <Select value={excludeFilter} onValueChange={setExcludeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="exclude-filter">
                  <SelectValue placeholder="Exclude from Discount" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="yes">Excluded (Yes)</SelectItem>
                  <SelectItem value="no">Not Excluded (No)</SelectItem>
                </SelectContent>
              </Select>
              
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="clear-filters-button">
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-auto max-h-[600px]">
              <Table containerClassName="overflow-visible">
                <TableHeader className="sticky top-0 z-20">
                  <TableRow className="bg-slate-100 hover:bg-slate-100">
                    <TableHead className="w-12 bg-slate-100 border-b-2 border-slate-200">
                      <Checkbox
                        checked={filteredProducts.length > 0 && selectedIds.length === filteredProducts.length}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-products-checkbox"
                      />
                    </TableHead>
                    <SortableHeader sortKey="product_name" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Product Name</SortableHeader>
                    <SortableHeader sortKey="sku_id" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">SKU ID</SortableHeader>
                    <TableHead className="bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Item Code</TableHead>
                    <SortableHeader sortKey="brand" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Brand</SortableHeader>
                    <SortableHeader sortKey="mrp" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">MRP</SortableHeader>
                    <SortableHeader sortKey="exclude_from_discount" sort={sort} onToggle={toggleSort} className="bg-slate-100 border-b-2 border-slate-200">Exclude from Discount</SortableHeader>
                    <TableHead className="text-right bg-slate-100 border-b-2 border-slate-200 font-semibold text-slate-700">Actions</TableHead>
                  </TableRow>
                </TableHeader>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      {products.length === 0 
                        ? "No products found. Add your first product to get started."
                        : "No products match your search/filter criteria."}
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedProducts.map((product) => (
                    <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.includes(product.id)}
                          onCheckedChange={(checked) => handleSelectOne(product.id, checked)}
                          data-testid={`select-product-${product.id}`}
                        />
                      </TableCell>
                      <TableCell className="font-medium">{product.product_name}</TableCell>
                      <TableCell className="font-mono text-sm">{product.sku_id}</TableCell>
                      <TableCell className="font-mono text-sm">{product.item_code || "-"}</TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-slate-100">
                          {product.brand}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono">₹{product.mrp.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>
                        {product.exclude_from_discount ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">Yes</span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">No</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(product)}
                            data-testid={`edit-product-${product.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => handleDelete(product.id)}
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}